import type { BrandFontFamily } from "../types";
import {
  type FontStyle,
  sanitizeFamilyName,
  sanitizeFileFontWeight,
  sanitizeStyle,
} from "./font-validation";

export type HostedFontVariant = { weight: string; style: FontStyle };
export type HostedFontFamily = {
  type: "hosted-css";
  name: string;
  cssUrl: string;
  variants: HostedFontVariant[];
};

export function isAdobeFontStylesheetUrl(value: string): boolean {
  return /^https:\/\/use\.typekit\.net\/[a-z0-9]+\.css$/.test(value);
}

export function normalizeAdobeFontStylesheetUrl(value: string): string | null {
  const input = value.trim();
  const url = /^[a-z0-9]+$/.test(input)
    ? `https://use.typekit.net/${input}.css`
    : input;
  return isAdobeFontStylesheetUrl(url) ? url : null;
}

function validVariant(
  weight: string | undefined,
  style: string | undefined,
): HostedFontVariant | null {
  const safeWeight = sanitizeFileFontWeight(weight);
  const safeStyle = sanitizeStyle(style);
  if (!safeWeight || !style || safeStyle.rejected) return null;
  const [minimum, maximum] = safeWeight.split(" ").map(Number);
  if (maximum !== undefined && minimum !== undefined && maximum < minimum)
    return null;
  return { weight: safeWeight, style: safeStyle.value };
}

export function hostedFontFromFamily(
  family: BrandFontFamily,
): HostedFontFamily | null {
  const name = sanitizeFamilyName(family.name);
  if (
    !name ||
    family.type !== "custom" ||
    !family.scopes?.includes("agents") ||
    !family.stylesheetUrl ||
    !isAdobeFontStylesheetUrl(family.stylesheetUrl)
  )
    return null;
  const variants = (family.stylesheetVariants ?? []).flatMap((variant) => {
    const valid = validVariant(variant.weight, variant.style);
    return valid ? [valid] : [];
  });
  return variants.length
    ? { type: "hosted-css", name, cssUrl: family.stylesheetUrl, variants }
    : null;
}

/** Only face identity is retained; Adobe continues to host the stylesheet and font resources. */
export function parseAdobeFontStylesheet(
  css: string,
): { name: string; variants: HostedFontVariant[] }[] {
  const families = new Map<string, HostedFontVariant[]>();
  const withoutComments = css.replace(/\/\*[\s\S]*?\*\//g, "");
  for (const match of withoutComments.matchAll(/@font-face\s*\{([^{}]*)\}/gi)) {
    const declarations = new Map(
      (match[1] ?? "").split(";").flatMap((declaration) => {
        const colon = declaration.indexOf(":");
        return colon < 0
          ? []
          : [
              [
                declaration.slice(0, colon).trim().toLowerCase(),
                declaration.slice(colon + 1).trim(),
              ] as const,
            ];
      }),
    );
    const name = sanitizeFamilyName(
      declarations.get("font-family")?.replace(/^(["'])(.*)\1$/, "$2"),
    );
    const variant = validVariant(
      declarations.get("font-weight"),
      declarations.get("font-style"),
    );
    if (!name || !variant) continue;
    const variants = families.get(name) ?? [];
    if (
      !variants.some(
        (existing) =>
          existing.weight === variant.weight &&
          existing.style === variant.style,
      )
    )
      variants.push(variant);
    families.set(name, variants);
  }
  return [...families].map(([name, variants]) => ({ name, variants }));
}

export function usedHostedFontNames(value: unknown, names: string[]): string[] {
  const used = new Set<string>();
  const compare = (stack: string) => {
    const families = stack
      .toLowerCase()
      .replace(/&quot;|&#39;|["']/g, "")
      .split(",")
      .map((family) => family.trim());
    for (const name of names)
      if (families.includes(name.toLowerCase())) used.add(name);
  };
  const visit = (node: unknown): void => {
    if (typeof node === "string") {
      for (const match of node.matchAll(/font-family\s*:\s*([^;}<>]+)/gi))
        compare(match[1] ?? "");
      for (const match of node.matchAll(/font-family\s*=\s*["']([^"']+)["']/gi))
        compare(match[1] ?? "");
    } else if (Array.isArray(node)) {
      for (const item of node) visit(item);
    } else if (node && typeof node === "object") {
      for (const [key, child] of Object.entries(node)) {
        if (
          (key === "fontFamily" || key === "font-family") &&
          typeof child === "string"
        )
          compare(child);
        else visit(child);
      }
    }
  };
  visit(value);
  return [...used];
}

export function isHostedFontVariantCovered(
  expected: HostedFontVariant,
  actual: HostedFontVariant[],
): boolean {
  const [low, high = low] = expected.weight.split(" ").map(Number);
  return actual.some((face) => {
    const [faceLow, faceHigh = faceLow] = face.weight.split(" ").map(Number);
    return (
      face.style === expected.style &&
      low !== undefined &&
      high !== undefined &&
      faceLow !== undefined &&
      faceHigh !== undefined &&
      low >= faceLow &&
      high <= faceHigh
    );
  });
}
